import styled, { createGlobalStyle, keyframes } from 'styled-components';
import SbButton from 'uiCore/SbButton/SbButton';

// ─── V2-only global fixes ─────────────────────────────────────────────────────
export const V2GlobalStyle = createGlobalStyle`
  body {
    font-family: ${p => p.theme?.typography?.fontFamily ?? 'Inter, system-ui, sans-serif'};
    font-size: 14px;
  }
`;

// ─── Root Layout Wrapper ─────────────────────────────────────────────────────

/* Layout Wrapper */
export const LayoutWrapperStyled = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${p => p.theme?.colors?.appBg ?? p.theme?.colors?.light ?? "#F7F7F7"};
`;

/* Header */
export const HeaderStyled = styled.header`
  height: 80px;
  flex-shrink: 0;
  background: ${p => p.theme?.colors?.brandGradient ?? p.theme?.colors?.primary ?? '#008080'};
  padding: 0 24px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;   /* positioning context for the decorative bridge motif */
  overflow: hidden;     /* clip the bridge to the header band */
`;

// ─── Decorative Bridge Motif ──────────────────────────────────────────────────
// The SkillsBridge "bridge" — one continuous line of big cloud-like arches (like the SkillsBridge
// footer). ONLY the line animates: it self-draws in, holds, then erases, and loops. The nodes are
// static constant dots — no fade, pulse, grow/shrink, glow or shadow.

// One-time draw on load: reveals from the path's start (the low-left point) upward, then stays.
const bridgeDraw = keyframes`
  from { stroke-dashoffset: 1; }   /* hidden */
  to   { stroke-dashoffset: 0; }   /* fully drawn (bottom → up), then holds */
`;

export const HeaderBridgeStyled = styled.div`
  position: absolute;
  inset: 0;                 /* span the full header band */
  z-index: 0;               /* behind the logo + actions (both raised to z-index: 1) */
  pointer-events: none;
  color: ${p => p.theme?.colors?.bridgeLine ?? '#88F3E0'};  /* single source for the line + node colour (matches the site) */

  svg {
    position: absolute;
    left: 110px;            /* start just past the ~100px logo, not behind it */
    top: -20px;
    height: 120px;          /* real proportions, bigger than the header (tops overflow) */
    width: auto;            /* width follows the real aspect — left-side, a bit wider now */
    display: block;
  }

  /* the self-writing line: draws itself in, holds, then erases — and loops forever */
  .bridge-span {
    fill: none;
    stroke: currentColor;
    stroke-width: 4;        /* viewBox units — renders ~1px thin at the fitted size */
    stroke-linecap: round;
    opacity: 0.9;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    /* hidden for ~4s after load, then self-draws once (bottom → up) and stays */
    animation: ${bridgeDraw} 2.5s ease-out 2s both;
  }

  /* static node points, like the site — a solid core + a faint constant halo ring (no animation) */
  .bridge-node {
    fill: currentColor;
  }
  .bridge-node-halo {
    fill: currentColor;
    opacity: 0.3;
  }

  /* Respect users who prefer no motion — show the final drawn state, no animation. */
  @media (prefers-reduced-motion: reduce) {
    .bridge-span { animation: none; stroke-dashoffset: 0; }
  }
`;

/* Body Wrapper */
export const BodyWrapperStyled = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;  /* Important for flex scrolling */
`;

/* Sidebar */
export const SiderStyled = styled.aside`
  width: 100px;
  background: ${p => p.theme?.colors?.brandGradient ?? p.theme?.colors?.primary ?? "#008080"};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* Sidebar Scroll */
export const SidebarScrollableStyled = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: ${p => p.theme?.colors?.borderLight ?? '#E5E7EB'} transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.theme?.colors?.borderLight ?? '#E5E7EB'};
    border-radius: 4px;
  }
`;

/* Content */
export const ContentStyled = styled.main`
  flex: 1;
  overflow: auto;
  padding: 12px;
  background: ${p => p.theme?.colors?.appBg ?? p.theme?.colors?.light ?? "#F7F7F7"};
  min-width: 0;  /* Prevent flex overflow */
`;

// ─── Sidebar Navigation Item ──────────────────────────────────────────────────

export const NavItemStyled = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
  color: ${p => p.theme?.colors?.white ?? '#ffffff'} !important;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
  min-height: 64px;
  text-align: center;
`;

export const NavIconWrapperStyled = styled.div`
  width: 48px;
  height: 48px;
  background: ${p => p.theme?.colors?.primary ?? '#008080'};
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 5px auto;
  font-size: 24px;   /* sizes the Ant icons (1em svg) to match the custom SVG icons */
  color: ${p => p.theme?.colors?.white ?? '#ffffff'};
   svg:not([data-stroke]) path { fill: ${p => p.theme?.colors?.white ?? '#ffffff'}; }

  .active & {
    background: ${p => p.theme?.colors?.white ?? '#ffffff'};
    color: ${p => p.theme?.colors?.primary ?? '#008080'};
    svg:not([data-stroke]) path { fill: ${p => p.theme?.colors?.primary ?? '#008080'}; }
  }
`;

export const SidebarFooterStyled = styled.div`
  padding: 0 0 0;
  background: transparent; /* let the SiderStyled brandGradient show through continuously */
  text-align: center;
  overflow-y: hidden;
  scrollbar-gutter: stable
`;

export const LegalNavItemStyled = styled(NavItemStyled)`
  margin-top: 0;
  padding-bottom: 0;
  margin-bottom: -6px;
`;

// ─── Logo Area ────────────────────────────────────────────────────────────────

export const LogoWrapperStyled = styled.div`
  width: 100px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  background: transparent;
  position: relative;   /* keep the logo above the bridge motif */
  z-index: 1;

  a {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-image {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }

  .ant-image-img,
  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    display: block;
  }
`;

// ─── Header Right Section ─────────────────────────────────────────────────────

export const NavActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;   /* keep the actions above the bridge motif */
  z-index: 1;
`;

// ─── Take-the-Tour Help Wrapper ───────────────────────────────────────────────
// Global help/tour trigger; mirrors the bell so the two sit consistently.
export const TourHelpWrapperStyled = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  opacity: 0.65;
  transition: opacity 200ms ease;
  svg {
    font-size: 18px;
    color: ${p => p.theme?.colors?.white ?? '#ffffff'};
  }
  &:hover {
    opacity: 1;
  }
`;

// ─── Notification Bell Wrapper ────────────────────────────────────────────────
export const BellWrapperStyled = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  svg {
    font-size: 24px;
  }
  svg path {
    color: ${p => p.theme?.colors?.white ?? '#ffffff'};
  }
  sup.ant-scroll-number span {
    color: white !important;
  }
`;

export const NotificationCountStyled = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background: ${p => p.theme?.colors?.danger ?? '#DC2626'};
  color: ${p => p.theme?.colors?.white ?? '#ffffff'};
  border-radius: 50%;

  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ─── Notification Dropdown ────────────────────────────────────────────────────

export const NotificationDropdownStyled = styled.div`
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  background: ${p => p.theme?.colors?.white ?? '#ffffff'};
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
`;

export const NotificationHeaderStyled = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  border-bottom: 1px solid ${p => p.theme?.colors?.border ?? '#D8DAE5'};
  .anticon-close {
   cursor: pointer;
  }
`;

export const NotificationItemStyled = styled.div`
  padding: 10px 16px;
  cursor: pointer;
`;

export const NotificationClearStyled = styled.div`
  padding: 8px 16px;
  text-align: right;
  border-top: 1px solid ${p => p.theme?.colors?.border ?? '#D8DAE5'};
  a {
    color: ${p => p.theme?.colors?.primary ?? '#008080'};
    cursor: pointer;
    &:hover {
      color: ${p => p.theme?.colors?.primaryHover ?? '#006666'};
    }
  }
`;

// ─── Profile Avatar ───────────────────────────────────────────────────────────

export const AvatarStyled = styled.div`
  width: 42px;      /* bigger */
  height: 42px;
  background-color: ${p => p.theme?.colors?.primary ?? "#008080"};
  color: ${p => p.theme?.colors?.white ?? '#ffffff'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  letter-spacing: 0.5px;
`;

// ─── Profile Dropdown Menu ────────────────────────────────────────────────────

export const ProfileMenuStyled = styled.div`
  min-width: 180px;
  background: ${p => p.theme?.colors?.white ?? '#ffffff'};
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  padding: 4px 0;
`;

export const ProfileMenuItemStyled = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  background: ${p => p.active ? (p.theme?.colors?.hoverBg ?? 'rgba(0, 128, 128, 0.1)') : 'transparent'};
  font-weight: ${p => p.active ? 600 : 500};

  &:hover {
    background: ${p => p.theme?.colors?.hoverBg ?? 'rgba(0, 128, 128, 0.1)'};
    color: ${p => p.theme?.colors?.primary ?? '#008080'};
  }
  color: ${p =>
   p.danger
     ? (p.theme?.colors?.danger ?? '#DC2626')
     : p.active
     ? (p.theme?.colors?.primary ?? "#008080")
     : (p.theme?.colors?.dark ?? "#2B3034")
  };
  svg {
    margin-right: 8px; 
  }
`;

export const ProfileMenuDividerStyled = styled.div`
  height: 1px;
  background: ${p => p.theme?.colors?.border ?? '#D8DAE5'};
  margin: 4px 0;
`;

export const ProfileNameStyled = styled.div`
  padding: 10px 16px 6px;
  border-bottom: 1px solid ${p => p.theme?.colors?.border ?? '#D8DAE5'};
  margin-bottom: 4px;
`;

export const ProfileFullNameStyled = styled.p`
  margin: 0;
`;

export const ProfileRoleStyled = styled.p`
  color: ${p => p.theme?.colors?.gray ?? '#565E70'};
  margin: 2px 0 0;
`;

// ─── Profile Role Switcher Label ─────────────────────────────────────────────

export const SwitchRoleLabelStyled = styled.div`
  padding: 4px 16px 2px;
  color: ${p => p.theme?.colors?.tertiary ?? '#8790A4'};
`;

// ─── Customer Logo Fallback Text ──────────────────────────────────────────────

export const CustomerLogoTextStyled = styled.span`
  color: ${p => p.theme?.colors?.gray ?? '#565E70'};
`;

// ─── UI Version Toggle ────────────────────────────────────────────────────────

export const UIToggleWrapperStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;   /* bigger */
  background: ${p => p.theme?.colors?.hoverBg ?? 'rgba(0, 128, 128, 0.1)'};
  border: 1px solid ${p => p.theme?.colors?.tertiary ?? '#8790A4'};
  border-radius: 8px;
`;

export const UIToggleLabelStyled = styled.span`
  color: ${p => p.theme?.colors?.gray ?? '#565E70'};
  white-space: nowrap;
`;

// ─── Feedback Button ──────────────────────────────────────────────────────────

export const FeedbackButtonWrapperStyled = styled.div`
  position: fixed;
  right: 48px;
  bottom: 102px;
  z-index: 4000;
`;

export const FeedbackButtonStyled = styled(SbButton)`
  background: ${p => p.theme?.colors?.white ?? '#ffffff'};
  border: 2px solid ${p => p.theme?.colors?.primary ?? '#008080'};
  border-radius: 8px;
  padding: 4px 8px 8px 8px;
  display: inline-flex;
 
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ThumbsContainerStyled = styled.span`
  position: relative;
  display: inline-block;
  width: 16px;
  height: 16px;
`;

// ─── Ask Allie (Dev-only floating widget; mutually exclusive with the ────────
// feedback button above, so it reuses the same bottom-right corner slot) ─────

export const AskAllieButtonWrapperStyled = styled.div`
  position: fixed;
  right: 48px;
  bottom: 102px;
  z-index: 4000;
`;

// ─── Sidebar Footer Logo ──────────────────────────────────────────────────────

export const SbIconStyled = styled.img`
  width: 90px;
  object-fit: contain;
  opacity: 0.85;
`;
